import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Input } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiX, FiImage, FiSave } = FiIcons;

const COLOR_PRESETS = [
  '#00AAFF', '#0088CC', '#1B4C87', '#10B981',
  '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899',
];

export default function AgencyTab() {
  const { user, setUser } = useAuth();
  const { addToast } = useToast();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [agencyLoading, setAgencyLoading] = useState(() => Boolean(user?.agency_id));
  const [logoPreview, setLogoPreview] = useState(null);
  const [form, setForm] = useState({
    name: user?.agency_name || '',
    primaryColor: user?.agency_primary_color || '#00AAFF',
    dailyCallGoal: '100',
    logo: null,
  });

  useEffect(() => {
    let cancelled = false;
    async function loadAgencyFromSupabase() {
      let agencyId = user?.agency_id;
      if (!agencyId) {
        try {
          const p = await api.auth.getCurrentUser();
          agencyId = p?.agency_id;
        } catch {
          /* keep agencyId null */
        }
      }
      if (!agencyId) {
        setAgencyLoading(false);
        setForm((f) => ({
          ...f,
          name: f.name || user?.agency_name || 'My Agency',
        }));
        return;
      }

      setAgencyLoading(true);
      try {
        const row = await api.agencies.getById(agencyId);
        if (cancelled || !row) return;
        const goal =
          typeof row.daily_call_goal === 'number' && row.daily_call_goal > 0
            ? row.daily_call_goal
            : 100;
        setForm((f) => ({
          ...f,
          name: row.name ?? 'My Agency',
          primaryColor: row.primary_color || '#00AAFF',
          dailyCallGoal: String(goal),
        }));
        setUser((prev) =>
          prev
            ? {
                ...prev,
                agency_id: agencyId,
                agency_name: row.name,
                agency_primary_color: row.primary_color,
              }
            : null
        );
      } catch (e) {
        console.error('[AgencyTab] Failed to load agency row:', e);
        setForm((f) => ({
          ...f,
          name: f.name || user?.agency_name || 'My Agency',
        }));
      } finally {
        if (!cancelled) setAgencyLoading(false);
      }
    }
    void loadAgencyFromSupabase();
    return () => {
      cancelled = true;
    };
  }, [user?.agency_id, user?.id, setUser]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      addToast({ type: 'error', title: 'File too large', message: 'Max 2MB allowed.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setForm((f) => ({ ...f, logo: file }));
  };

  const handleSave = async () => {
    let freshProfile = null;
    try {
      freshProfile = await api.auth.getCurrentUser();
    } catch (e) {
      console.error('[AgencyTab] getCurrentUser() failed while preparing save:', e);
    }

    const agencyId = freshProfile?.agency_id ?? user?.agency_id;

    if (!form.name.trim()) {
      addToast({ type: 'error', title: 'Agency name is required' });
      return;
    }

    const goalRaw = form.dailyCallGoal.replace(/\D/g, '');
    const dailyCallGoal = parseInt(goalRaw, 10);
    if (!goalRaw || !Number.isFinite(dailyCallGoal) || dailyCallGoal < 1) {
      addToast({
        type: 'error',
        title: 'Invalid Daily Call Goal',
        message: 'Enter a positive whole number (1 or greater).',
      });
      return;
    }

    const updates = {
      name: form.name.trim(),
      primary_color: form.primaryColor,
      daily_call_goal: dailyCallGoal,
    };
    if (!agencyId) {
      addToast({
        type: 'error',
        title: 'Cannot save agency',
        message: 'Your profile has no agency id. Contact support.',
      });
      return;
    }

    setSaving(true);
    try {
      const updated = await api.agencies.update(agencyId, updates);

      const savedGoal =
        typeof updated.daily_call_goal === 'number' && updated.daily_call_goal > 0
          ? updated.daily_call_goal
          : dailyCallGoal;
      setForm((f) => ({ ...f, dailyCallGoal: String(savedGoal) }));

      setUser((prev) =>
        prev
          ? {
              ...prev,
              agency_id: prev.agency_id ?? agencyId,
              agency_name: updated.name,
              agency_primary_color: updated.primary_color,
            }
          : freshProfile
            ? {
                ...freshProfile,
                agency_name: updated.name,
                agency_primary_color: updated.primary_color,
              }
            : null
      );
      addToast({ type: 'success', title: 'Agency updated', message: 'Your changes have been saved.' });
    } catch (err) {
      console.error('[AgencyTab] api.agencies.update failed:', err);
      addToast({
        type: 'error',
        title: 'Failed to save changes',
        message: err?.message || 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Agency Name */}
      <Card className="p-6 space-y-5">
        <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">Agency Details</h2>
        <Input
          label="Agency Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Acme Insurance Group"
          disabled={agencyLoading}
        />

        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
          <div className="flex items-center gap-3 flex-wrap">
            {COLOR_PRESETS.map((color) => (
              <button
                key={color}
                type="button"
                disabled={agencyLoading}
                onClick={() => setForm((f) => ({ ...f, primaryColor: color }))}
                className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                  form.primaryColor === color ? 'border-gray-800 scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
            <div className="flex items-center gap-2 ml-2">
              <input
                type="color"
                value={form.primaryColor}
                onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
                disabled={agencyLoading}
                className="w-8 h-8 rounded cursor-pointer border border-gray-200 disabled:opacity-50"
                title="Custom color"
              />
              <span className="text-xs text-gray-500 font-mono">{form.primaryColor}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div
              className="h-8 px-4 rounded-md text-white text-sm font-medium flex items-center"
              style={{ backgroundColor: form.primaryColor }}
            >
              Preview Button
            </div>
            <span className="text-xs text-gray-400">Color preview</span>
          </div>
        </div>

        <Input
          label="Daily Call Goal"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={form.dailyCallGoal}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            setForm((f) => ({ ...f, dailyCallGoal: digits }));
          }}
          placeholder="e.g. 100"
          disabled={agencyLoading}
        />
        <p className="text-xs text-gray-500 -mt-3">
          Target outbound calls per day for your team (positive whole numbers only).
        </p>
      </Card>

      {/* Logo Upload */}
      <Card className="p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">Agency Logo</h2>
        <div className="flex items-start gap-6">
          {/* Preview */}
          <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 flex-shrink-0 overflow-hidden">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
            ) : (
              <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-300" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-3">
              Upload your agency logo. Recommended size: 256×256px. Max 2MB. PNG or SVG preferred.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                <SafeIcon icon={FiUpload} className="mr-2 w-4 h-4" />
                Upload Logo
              </Button>
              {logoPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setLogoPreview(null); setForm((f) => ({ ...f, logo: null })); }}
                >
                  <SafeIcon icon={FiX} className="mr-1 w-4 h-4" />
                  Remove
                </Button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />
          </div>
        </div>
      </Card>

      <form
        className="flex justify-end"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <Button type="submit" disabled={saving || agencyLoading}>
          {saving ? <><Spinner className="mr-2" />Saving…</> : <><SafeIcon icon={FiSave} className="mr-2" />Save Changes</>}
        </Button>
      </form>
    </div>
  );
}